import { useMemo } from 'react';
import { MESSAGE_TYPE_ENUM } from '../constants';
import { IAssistantMessage, IQuestionOption } from '../types';

// Grill question optionType values
export const GRILL_OPTION_TYPES = {
  BOTTOM_SASH: 23,
  TOP_SASH: 24,
  LEAD_TOP_SASH: 36,
  LEAD_BOTTOM_SASH: 37,
  ARCH_TOP_SASH: 38,
  ARCH_BOTTOM_SASH: 39,
} as const;

// Top sash variants (24, 36, 38)
const GRILL_TOP_TYPES = new Set<number>([
  GRILL_OPTION_TYPES.TOP_SASH,
  GRILL_OPTION_TYPES.LEAD_TOP_SASH,
  GRILL_OPTION_TYPES.ARCH_TOP_SASH,
]);

// Bottom sash variants (23, 37, 39)
const GRILL_BOTTOM_TYPES = new Set<number>([
  GRILL_OPTION_TYPES.BOTTOM_SASH,
  GRILL_OPTION_TYPES.LEAD_BOTTOM_SASH,
  GRILL_OPTION_TYPES.ARCH_BOTTOM_SASH,
]);

const ALL_GRILL_TYPES = new Set<number>([...GRILL_TOP_TYPES, ...GRILL_BOTTOM_TYPES]);

export interface IGrillWindow {
  /** e.g. "Window 1", "Window 2", or "Window 1" for single-window configs */
  windowLabel: string;
  /** Selected top sash grill option (thumbnail = grill image URI) */
  topSash: IQuestionOption | null;
  /** Selected bottom sash grill option (thumbnail = grill image URI) */
  bottomSash: IQuestionOption | null;
}

/**
 * Extracts "Window N" for a grill question message.
 *
 * Priority:
 *  1. responsePath suffix — per-frame coupled/bay questions encode the frame
 *     number as `_frame\d+` or `frame\d+` at the end of the responsePath
 *     (e.g. "cat.23.5_frame2" → Window 2). Most reliable — no reliance on
 *     question wording.
 *  2. Question text — looks for "window N" or "frame N" as a fallback for
 *     configs where the frame number is written into the question string.
 *  3. "Window 1" — single-window / same-for-all questions.
 */
const extractWindowLabel = (questionText: string, responsePath?: string): string => {
  // 1. responsePath: match trailing _frame\d+ or frame\d+
  if (responsePath) {
    const rpMatch = responsePath.match(/frame(\d+)$/i);
    if (rpMatch) return `Window ${rpMatch[1]}`;
  }
  // 2. Question text: "window N" or "frame N"
  const textMatch = questionText.match(/(?:window|frame)\s*(\d+)/i);
  if (textMatch) return `Window ${textMatch[1]}`;
  // 3. Default
  return 'Window 1';
};

/**
 * Hook that scans assistant messages for grill answers (optionType 23, 24, 36, 37, 38, 39)
 * and returns a sorted array of per-window top/bottom sash grill selections.
 *
 * Usage:
 *   const grillWindows = useGrillAnswers(cartItem.messages);
 *   // grillWindows[0].topSash.thumbnail  → top grill image URI
 *   // grillWindows[0].bottomSash.thumbnail → bottom grill image URI
 */
const DECORATIVE_BAR_TYPE_OPTION = 63;

export const useGrillAnswers = (
  messages: IAssistantMessage[],
  frameCount: number = 1,
): IGrillWindow[] => {
  return useMemo(() => {
    // If the parent "Decorative Bar Type" question (optionType 63) is answered as "None",
    // no grills apply — return empty immediately to reset any previous grill blend.
    const parentMsg = messages.find(
      m => m.type === MESSAGE_TYPE_ENUM.user && m.question?.optionType === DECORATIVE_BAR_TYPE_OPTION
    );
    if (parentMsg?.response?.answer?.[0]?.responseValue === 'none') {
      return [];
    }

    const windowMap = new Map<string, IGrillWindow>();
    let sawFrameSuffix = false;

    for (const msg of messages) {
      if (msg.type !== MESSAGE_TYPE_ENUM.user) continue;

      const optionType = msg.question?.optionType;
      if (!optionType || !ALL_GRILL_TYPES.has(optionType)) continue;

      // Use the first selected answer
      const answer: IQuestionOption | null = msg.response?.answer?.[0] ?? null;
      if (!answer) continue;

      if (/frame\d+$/i.test(msg.question?.responsePath ?? '')) sawFrameSuffix = true;
      const windowLabel = extractWindowLabel(msg.question?.question ?? '', msg.question?.responsePath);

      if (!windowMap.has(windowLabel)) {
        windowMap.set(windowLabel, { windowLabel, topSash: null, bottomSash: null });
      }

      const entry = windowMap.get(windowLabel)!;
      const isNone = answer.optionText.toLowerCase() === 'none';

      if (GRILL_TOP_TYPES.has(optionType)) {
        entry.topSash = isNone ? null : answer;
      } else if (GRILL_BOTTOM_TYPES.has(optionType)) {
        entry.bottomSash = isNone ? null : answer;
      }
    }

    // "Same for All Windows" mode asks the grill question once, with no
    // per-frame responsePath suffix, so every answer lands under the single
    // default "Window 1" label regardless of the product's real frame count.
    // Without expanding it here, the caller (useGrillBlend) sees exactly one
    // window and overlays the chosen style across the WHOLE multi-frame image
    // via the single-frame endpoint instead of tiling it onto every physical
    // frame. Replicate the one answered entry across every real frame.
    const single = windowMap.size === 1 ? windowMap.values().next().value : undefined;
    if (single && !sawFrameSuffix && frameCount > 1) {
      return Array.from({ length: frameCount }, (_, i) => ({
        windowLabel: `Window ${i + 1}`,
        topSash: single.topSash,
        bottomSash: single.bottomSash,
      }));
    }

    // Sort numerically: Window 1 < Window 2 < Window 10 …
    return Array.from(windowMap.values()).sort((a, b) =>
      a.windowLabel.localeCompare(b.windowLabel, undefined, { numeric: true })
    );
  }, [messages, frameCount]);
};
